import React, { useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { includes } from 'lodash';
import {
  MINIMUM_POSTAL_CODE_LENGTH,
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  SHUFFLE_REVERSE,
  SHUFFLE_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
} from '../../constants';
import ABConceptAttributes from './AbConceptAttributes';
import QuestionMedia from '../QuestionMedia';
import { transformMediaQuestionAttributes } from './utils';
import DescriptiveText from './DescriptiveText';
import SubmitButton from '../SubmitButton';
import Answers from '../Answers';
import QuestionBody from '../QuestionBody';
import QuestionPrompt from '../QuestionPrompt';
import { shuffleAnswers } from '../Answers/utils';
import {
  getCarriedForwardScorepoints,
  getScorepointShuffleType,
  shuffleArray,
} from '../../utils';

export default function Question({ question }) {
  const { getText } = Pollfish.ui;
  const { answers, style, shuffle: shuffleType } = question;

  const mediaAttributes = transformMediaQuestionAttributes({
    ...question,
    questionRefId: question.refId,
  });

  const submitBtnProps = {
    onTap: Pollfish.ui.loadQuestionOrThankScreen.bind(Pollfish.ui),
  };
  if (style === QUESTION_OPEN_ENDED && question.f_name === 'dg_postal_data') {
    submitBtnProps.onTap = () => {
      const postalCodeValue = Pollfish.helper.getPostalCodeValue();
      const selectedPostalCode = Pollfish.helper.getPostalCodeSelection();
      const selectedPostalCodeCountry = selectedPostalCode.attr(
        'data-postal-code-country',
      );
      const selectedPostalCodePlaceId = selectedPostalCode.attr(
        'data-postal-code-place-id',
      );
      const selectedPostalCodePreferNotToSay =
        selectedPostalCode.attr('data-postal-code-prefer-not-to-say') ===
        'true';
      if (
        postalCodeValue.length >= MINIMUM_POSTAL_CODE_LENGTH &&
        selectedPostalCode.length &&
        !selectedPostalCodeCountry &&
        !selectedPostalCodePlaceId &&
        !selectedPostalCodePreferNotToSay
      ) {
        Pollfish.ui.showConfirmationModal({
          useHtmlContent: true,
          header: getText('warnings.noZipcode'),
          content: getText('warnings.verifyCustomZipcode').replace(
            'XXXXX',
            `<strong>${postalCodeValue}</strong>`,
          ),
          noBtnText: getText('tryAgain'),
          yesBtnText: getText('submit'),
          onConfirm: () => {
            Pollfish.ui.loadQuestionOrThankScreen();
          },
          style: {
            maxWidth: 400,
          },
          buttonStack: 'vertical',
        });
      } else {
        Pollfish.ui.loadQuestionOrThankScreen();
      }
    };
  }

  $('body').attr('data-page', `question-${question.id}`);
  if (question.f_name) {
    $('body').attr('data-f_name', question.f_name);
  } else {
    $('body').removeAttr('data-f_name');
  }

  // We should shuffle answers only once for the following question types
  // in order to keep the same order in both QuestionBody and Answers components
  const canShuffleAnswers =
    style === QUESTION_SINGLE ||
    style === QUESTION_MULTIPLE ||
    style === QUESTION_RANKING ||
    style === QUESTION_MATRIX_SINGLE ||
    style === QUESTION_MATRIX_MULTIPLE;

  // We should shuffle score points only once for the following question types
  // in order to keep the same order in both QuestionBody and Answers components
  const canShuffleScorepoints =
    style === QUESTION_MATRIX_SINGLE || style === QUESTION_MATRIX_MULTIPLE;

  const shuffledScorepoints = useMemo(() => {
    if (!canShuffleScorepoints) {
      return [];
    }
    const transformedScorepoints = getCarriedForwardScorepoints(question);

    // At the moment, the selected answers are kept in DOM, as indexes
    // and we get the scorePoint id based on the index of the array question.scorepoints
    // But now, every answer can have different order on the scorepoint array,
    // so we override this array, and push every answer's ordering
    // eslint-disable-next-line
    let newScorepoints = [];

    let data;
    answers.forEach((_, idx) => {
      const normalizedShuffleType = getScorepointShuffleType(shuffleType);
      const hasReverseShuffleType = includes(
        [
          SHUFFLE_REVERSE,
          SHUFFLE_REVERSE_WITH_LAST_FIXED,
          SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
        ],
        normalizedShuffleType,
      );

      // for reverse shuffle, do the shuffling only once so that the
      // order of scorepoints for each answer is the same
      if (!hasReverseShuffleType || (hasReverseShuffleType && idx === 0)) {
        data = shuffleArray(transformedScorepoints, normalizedShuffleType);
      }
      // shuffle scorepoints per answer
      newScorepoints.push([...data]);
    });

    return newScorepoints;
  }, [question, answers, canShuffleScorepoints, shuffleType]);

  const shuffledAnswers = useMemo(() => {
    if (!canShuffleAnswers) {
      return answers;
    }
    return shuffleAnswers(question);
  }, [canShuffleAnswers, question, answers]);

  const { groups } = Pollfish.survey;
  return (
    <Fragment>
      <div className="main-content__holder">
        <QuestionPrompt question={question} />
        <div className="answers-wrapper">
          <DescriptiveText
            isSanityQuestion={question.isSanityQuestion}
            getText={getText}
          />
          <ABConceptAttributes groups={groups} question={question} />
          <QuestionMedia {...mediaAttributes} />
          <QuestionBody
            question={question}
            answers={shuffledAnswers}
            scorepoints={shuffledScorepoints}
          />
          <Answers question={question} answers={shuffledAnswers} />
        </div>
      </div>
      <SubmitButton {...submitBtnProps} />
    </Fragment>
  );
}

Question.propTypes = {
  question: PropTypes.object,
};
